import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { DollarSign, ChevronDown, ChevronRight, Loader2, RefreshCw, Info } from 'lucide-react';
import { log } from '../lib/logger';

// Fixed clinic list (mirrors Layout.jsx). Kept in-page so the filter
// works even when the OD only sees a subset — backend scopes anyway.
const CLINICS = ['Katy', 'Missouri City', 'Townelake', 'Fry Rd', 'Memorial',
                 'Brookshire', 'Tomball', 'Magnolia', 'Atascocita'];

const CAP_PRESETS = [5, 10, 15, 20, 30];

function fmtCurrency(v) {
  if (v == null) return '—';
  return `$${Number(v).toFixed(2)}`;
}

function fmtDate(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function LowDeductibleReport() {
  const { user } = useAuth();
  const [cap, setCap] = useState(15);
  const [scope, setScope] = useState('180d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [clinician, setClinician] = useState('');
  const [clinic, setClinic] = useState('');
  const [clinicianOptions, setClinicianOptions] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // group key → true

  const load = useCallback(async () => {
    if (scope === 'custom' && !customStart) return; // don't fire until start picked
    setLoading(true);
    try {
      const params = { cap, scope };
      if (scope === 'custom') {
        params.start = customStart;
        if (customEnd) params.end = customEnd;
      }
      if (clinician) params.clinician = clinician;
      if (clinic) params.clinic = clinic;
      const r = await api.get('/low-deductible/report', { params });
      setData(r.data);
      const init = {};
      (r.data.groups || []).slice(0, 2).forEach(g => { init[g.clinician] = true; });
      setExpanded(init);
    } catch (e) {
      log.error('low-deductible report failed', e);
      toast.error(e?.response?.data?.detail || 'Report failed to load');
    } finally { setLoading(false); }
  }, [cap, scope, customStart, customEnd, clinician, clinic]);

  useEffect(() => {
    // Reload clinician dropdown whenever scope, custom range, or clinic
    // changes so it only shows clinicians for the current window/clinic.
    if (scope === 'custom' && !customStart) return;
    (async () => {
      try {
        const params = { scope };
        if (scope === 'custom') {
          params.start = customStart;
          if (customEnd) params.end = customEnd;
        }
        if (clinic) params.clinic = clinic;
        const r = await api.get('/low-deductible/clinicians', { params });
        const list = r.data.clinicians || [];
        setClinicianOptions(list);
        if (clinician && !list.includes(clinician)) {
          setClinician('');
        }
      } catch (e) { log.warn('clinician list load failed', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, customStart, customEnd, clinic]);

  useEffect(() => { load(); }, [load]);

  const scopeLabel = useMemo(() => {
    const sc = data?.scope;
    if (!sc) return '';
    const fmt = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const label = { '30d': 'Last 30 days', '90d': 'Last 90 days',
                    '180d': 'Last 180 days', 'ytd': 'Year to date',
                    'custom': 'Custom range' }[sc.scope] || sc.scope;
    return `${label} · ${fmt(sc.start)} → ${sc.end ? fmt(sc.end) : 'today'}`;
  }, [data]);

  const toggle = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="low-deductible-page">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#F1E7D6] flex items-center justify-center" style={{ boxShadow: 'inset 0 0 0 1px #E4D5B9' }}>
              <DollarSign size={20} style={{ color: '#8A6A2B' }} />
            </div>
            <div>
              <h1 className="text-2xl font-serif text-[#2D2833]">Low-Deductible Report</h1>
              <div className="text-xs text-[#685B72] mt-1">
                Clients whose <strong>latest</strong> out-of-pocket per session is at or below your cap — perfect for a "let's add another visit per week" call.
              </div>
            </div>
          </div>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          variant="outline"
          className="rounded-full text-xs font-semibold"
          data-testid="ldr-refresh-btn"
        >
          {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Loading…</> : <><RefreshCw size={14} className="mr-2" /> Refresh</>}
        </Button>
      </div>

      {/* ── Filters ───────────────────────────────────────────────── */}
      <Card className="rounded-2xl border p-5" style={{ borderColor: '#E8E5EC', background: '#FBF9FD' }} data-testid="ldr-filters">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72] mb-2">
              Deductible cap (latest session)
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A6A2B] text-sm font-semibold">$</span>
                <Input
                  type="number"
                  min={0}
                  max={500}
                  step={1}
                  value={cap}
                  onChange={e => setCap(Math.max(0, Number(e.target.value) || 0))}
                  className="pl-7 rounded-full bg-white"
                  data-testid="ldr-cap-input"
                />
              </div>
            </div>
            <div className="flex gap-1 mt-2">
              {CAP_PRESETS.map(p => (
                <button
                  key={p}
                  onClick={() => setCap(p)}
                  className={`text-[11px] px-2 py-1 rounded-full border font-semibold transition ${
                    cap === p
                      ? 'bg-[#8A6A2B] text-white border-[#8A6A2B]'
                      : 'bg-white text-[#685B72] border-[#E8E5EC] hover:border-[#8A6A2B]'
                  }`}
                  data-testid={`ldr-cap-preset-${p}`}
                >
                  ${p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72] mb-2">
              Date range
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { v: '30d', l: 'Last 30 days' },
                { v: '90d', l: 'Last 90 days' },
                { v: '180d', l: 'Last 180 days' },
                { v: 'ytd', l: 'YTD' },
                { v: 'custom', l: 'Custom' },
              ].map(o => (
                <button
                  key={o.v}
                  onClick={() => setScope(o.v)}
                  className={`text-[11px] px-3 py-1.5 rounded-full border font-semibold transition ${
                    scope === o.v
                      ? 'bg-[#8A6A2B] text-white border-[#8A6A2B]'
                      : 'bg-white text-[#685B72] border-[#E8E5EC] hover:border-[#8A6A2B]'
                  }`}
                  data-testid={`ldr-scope-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
            {scope === 'custom' && (
              <div className="flex items-center gap-2 mt-2" data-testid="ldr-custom-range">
                <Input
                  type="date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={e => setCustomStart(e.target.value)}
                  className="rounded-full bg-white text-xs"
                  data-testid="ldr-custom-start"
                />
                <span className="text-xs text-[#685B72]">→</span>
                <Input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  onChange={e => setCustomEnd(e.target.value)}
                  className="rounded-full bg-white text-xs"
                  data-testid="ldr-custom-end"
                />
              </div>
            )}
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72] mb-2">
              Clinic
            </div>
            <select
              value={clinic}
              onChange={e => setClinic(e.target.value)}
              className="w-full rounded-full bg-white border border-[#E8E5EC] px-4 py-2 text-sm text-[#2D2833] focus:outline-none focus:border-[#8A6A2B]"
              data-testid="ldr-clinic-select"
            >
              <option value="">All clinics</option>
              {CLINICS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72] mb-2">
              Clinician
            </div>
            <select
              value={clinician}
              onChange={e => setClinician(e.target.value)}
              className="w-full rounded-full bg-white border border-[#E8E5EC] px-4 py-2 text-sm text-[#2D2833] focus:outline-none focus:border-[#8A6A2B]"
              data-testid="ldr-clinician-select"
            >
              <option value="">All clinicians</option>
              {clinicianOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {scopeLabel && (
          <div className="mt-4 pt-4 border-t border-[#EAE3F0] flex items-center gap-2 text-[11px] text-[#685B72]">
            <Info size={12} />
            <span>Scope: {scopeLabel} · Treatment codes only (92507 / 92609 / 92610) · Latest charge per client</span>
          </div>
        )}
      </Card>

      {/* ── Summary strip ─────────────────────────────────────────── */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ldr-summary">
          <Card className="rounded-2xl border p-5" style={{ borderColor: '#E8E5EC', background: 'white' }}>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72]">
              Clients at or under ${cap}
            </div>
            <div className="text-4xl font-serif text-[#2D2833] mt-1" data-testid="ldr-total-count">
              {data.total_clients}
            </div>
          </Card>
          <Card className="rounded-2xl border p-5" style={{ borderColor: '#E8E5EC', background: 'white' }}>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72]">
              Clinicians with candidates
            </div>
            <div className="text-4xl font-serif text-[#2D2833] mt-1">
              {data.groups?.length || 0}
            </div>
          </Card>
          <Card className="rounded-2xl border p-5" style={{ borderColor: '#E8E5EC', background: 'white' }}>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#685B72]">
              Biggest opportunity
            </div>
            <div className="text-lg font-semibold text-[#2D2833] mt-1 truncate">
              {data.groups?.[0]?.clinician || '—'}
            </div>
            <div className="text-xs text-[#685B72] mt-0.5">
              {data.groups?.[0]?.count ? `${data.groups[0].count} clients` : 'No data'}
            </div>
          </Card>
        </div>
      )}

      {loading && !data && (
        <Card className="rounded-2xl border p-12 text-center" style={{ borderColor: '#E8E5EC' }}>
          <Loader2 size={24} className="animate-spin mx-auto text-[#685B72]" />
          <div className="mt-3 text-sm text-[#685B72]">Building your report…</div>
        </Card>
      )}

      {data && !data.groups?.length && !loading && (
        <Card className="rounded-2xl border p-12 text-center" style={{ borderColor: '#E8E5EC' }} data-testid="ldr-empty">
          <div className="text-lg font-serif text-[#2D2833]">No clients under ${cap}</div>
          <div className="text-sm text-[#685B72] mt-2">
            Try raising the cap, expanding the window, or removing the clinician/clinic filter.
          </div>
        </Card>
      )}

      {data?.groups?.map(g => {
        const isOpen = !!expanded[g.clinician];
        return (
          <Card
            key={g.clinician}
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: '#E8E5EC' }}
            data-testid={`ldr-group-${g.clinician.replace(/\s+/g, '-')}`}
          >
            <button
              onClick={() => toggle(g.clinician)}
              className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FBF9FD] transition text-left"
              data-testid={`ldr-group-toggle-${g.clinician.replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <div>
                  <div className="text-base font-semibold text-[#2D2833]">{g.clinician}</div>
                  <div className="text-[11px] text-[#685B72] mt-0.5">
                    {g.count} client{g.count === 1 ? '' : 's'} · lowest {fmtCurrency(g.min_latest)}
                  </div>
                </div>
              </div>
              <span className="rounded-full text-[11px] font-bold px-2.5 py-1"
                    style={{ background: '#F1E7D6', color: '#8A6A2B' }}>
                {g.count}
              </span>
            </button>
            {isOpen && (
              <div className="border-t" style={{ borderColor: '#EAE3F0' }}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid={`ldr-table-${g.clinician.replace(/\s+/g, '-')}`}>
                    <thead style={{ background: '#FBF9FD' }}>
                      <tr className="text-[10px] uppercase tracking-widest text-[#685B72]">
                        <th className="text-left px-5 py-2 font-semibold">Client</th>
                        <th className="text-left px-3 py-2 font-semibold">Clinic</th>
                        <th className="text-right px-3 py-2 font-semibold">Deductible</th>
                        <th className="text-right px-3 py-2 font-semibold">Sessions</th>
                        <th className="text-right px-5 py-2 font-semibold">Last visit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.clients.map(c => (
                        <tr key={c.client} className="border-t border-[#EAE3F0] hover:bg-[#FBF9FD]" data-testid={`ldr-row-${c.client}`}>
                          <td className="px-5 py-2.5 font-medium text-[#2D2833]">{c.client}</td>
                          <td className="px-3 py-2.5 text-[#685B72]">{c.clinic}</td>
                          <td className="px-3 py-2.5 text-right font-semibold" style={{ color: c.latest_charge <= 5 ? '#4B7A5C' : c.latest_charge <= 10 ? '#8A6A2B' : '#2D2833' }}>
                            {fmtCurrency(c.latest_charge)}
                          </td>
                          <td className="px-3 py-2.5 text-right text-[#685B72]">{c.sessions}</td>
                          <td className="px-5 py-2.5 text-right text-[#685B72] text-xs">{fmtDate(c.last_dos)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
